import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CrmDashboardSummary,
  CrmDeal,
  CrmForecast,
  CrmLeadRequest,
  CrmLeadResponse,
  CrmPipeline,
  CrmCompany,
  Customer360,
  CrmEmailAccount,
  CrmEmailAccountRequest,
} from '@/types/crm';

export const crmApi = {
  listLeads: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmLeadResponse>>>('/v1/crm/leads', { params })),

  listLeadBoard: (pipelineId?: number) =>
    unwrap(api.get<ApiResponse<CrmLeadResponse[]>>('/v1/crm/leads/board', { params: { pipelineId } })),

  createLead: (body: CrmLeadRequest) =>
    unwrap(api.post<ApiResponse<CrmLeadResponse>>('/v1/crm/leads', body)),

  getLead: (id: number) =>
    unwrap(api.get<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}`)),

  updateLead: (id: number, body: CrmLeadRequest) =>
    unwrap(api.put<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}`, body)),

  moveLeadStage: (id: number, stageId: number) =>
    unwrap(api.post<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}/stages/${stageId}`)),

  deleteLead: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/crm/leads/${id}`)),

  convertLead: (id: number, body: import('@/types/crm').CrmConvertLeadRequest) =>
    unwrap(api.post<ApiResponse<CrmLeadResponse>>(`/v1/crm/leads/${id}/convert`, body)),

  listLeadCommunications: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmLeadCommunication[]>>(`/v1/crm/leads/${id}/communications`)),

  createLeadCommunication: (
    id: number,
    body: { channelCode: string; direction?: string; subject?: string; body: string; attachmentJson?: string; internalNote?: boolean },
  ) => unwrap(api.post<ApiResponse<import('@/types/crm').CrmLeadCommunication>>(`/v1/crm/leads/${id}/communications`, body)),

  listLeadQuotations: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmSalesQuotation[]>>(`/v1/crm/leads/${id}/quotations`)),

  submitQuotation: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSalesQuotation>>(`/v1/crm/quotations/${id}/submit`)),

  approveQuotation: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSalesQuotation>>(`/v1/crm/quotations/${id}/approve`)),

  generateQuotationPdf: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSalesQuotation>>(`/v1/crm/quotations/${id}/pdf`)),

  shareQuotation: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSalesQuotation>>(`/v1/crm/quotations/${id}/share`)),

  convertQuotation: (id: number, salesOrderId: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSalesQuotation>>(`/v1/crm/quotations/${id}/convert`, null, { params: { salesOrderId } })),

  bulkCreateLeads: (leads: CrmLeadRequest[]) =>
    unwrap(api.post<ApiResponse<{ successCount: number; failureCount: number }>>('/v1/crm/leads/bulk', { leads })),

  dashboardSummary: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'MONTH') =>
    unwrap(api.get<ApiResponse<CrmDashboardSummary>>('/v1/crm/dashboard/summary', { params: { period } })),

  listPipelines: () => unwrap(api.get<ApiResponse<CrmPipeline[]>>('/v1/crm/pipelines')),

  ensureDefaultPipeline: () =>
    unwrap(api.post<ApiResponse<CrmPipeline>>('/v1/crm/pipelines/ensure-default')),

  listCompanies: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmCompany>>>('/v1/crm/companies', { params })),

  createCompany: (body: Partial<CrmCompany>) =>
    unwrap(api.post<ApiResponse<CrmCompany>>('/v1/crm/companies', body)),

  getCompany: (id: number) =>
    unwrap(api.get<ApiResponse<CrmCompany>>(`/v1/crm/companies/${id}`)),

  updateCompany: (id: number, body: Partial<CrmCompany>) =>
    unwrap(api.put<ApiResponse<CrmCompany>>(`/v1/crm/companies/${id}`, body)),

  deleteCompany: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/crm/companies/${id}`)),

  listContacts: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<import('@/types/crm').CrmContact>>>('/v1/crm/contacts', { params })),

  getContact: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmContact>>(`/v1/crm/contacts/${id}`)),

  createContact: (body: Partial<import('@/types/crm').CrmContact>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmContact>>('/v1/crm/contacts', body)),

  updateContact: (id: number, body: Partial<import('@/types/crm').CrmContact>) =>
    unwrap(api.put<ApiResponse<import('@/types/crm').CrmContact>>(`/v1/crm/contacts/${id}`, body)),

  deleteContact: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/crm/contacts/${id}`)),

  listContactDuplicates: (params: { email?: string; phone?: string; mobile?: string }) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmContact[]>>('/v1/crm/contacts/duplicates', { params })),

  mergeContacts: (body: { sourceContactId: number; targetContactId: number; mergeReason?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmContact>>('/v1/crm/contacts/merge', body)),

  listDeals: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CrmDeal>>>('/v1/crm/deals', { params })),

  createDeal: (body: Partial<CrmDeal>) =>
    unwrap(api.post<ApiResponse<CrmDeal>>('/v1/crm/deals', body)),

  moveDealStage: (dealId: number, stageId: number) =>
    unwrap(api.post<ApiResponse<CrmDeal>>(`/v1/crm/deals/${dealId}/stages/${stageId}`)),

  listDealStageHistory: (dealId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmDealStageHistory[]>>(`/v1/crm/deals/${dealId}/stage-history`)),

  approveDealWin: (dealId: number) =>
    unwrap(api.post<ApiResponse<CrmDeal>>(`/v1/crm/deals/${dealId}/approve-win`)),

  customer360: (customerId: number) =>
    unwrap(api.get<ApiResponse<Customer360>>(`/v1/crm/customers/${customerId}/360`)),

  forecast: () => unwrap(api.get<ApiResponse<CrmForecast>>('/v1/crm/forecast')),

  reports: () => unwrap(api.get<ApiResponse<import('@/types/crm').CrmReportsBundle>>('/v1/crm/reports')),

  exportReports: () => unwrap(api.get<ApiResponse<string>>('/v1/crm/reports/export')),

  listScheduledReports: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmScheduledReport[]>>('/v1/crm/reports/scheduled')),

  scheduleReport: (body: Partial<import('@/types/crm').CrmScheduledReport>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmScheduledReport>>('/v1/crm/reports/scheduled', body)),

  listCampaigns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<import('@/types/crm').CrmCampaign>>>('/v1/crm/campaigns', { params })),

  createCampaign: (body: Partial<import('@/types/crm').CrmCampaign> & { name: string; channel: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmCampaign>>('/v1/crm/campaigns', body)),

  launchCampaign: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmCampaign>>(`/v1/crm/campaigns/${id}/launch`)),

  scheduleCampaign: (id: number, scheduledAt: string) =>
    unwrap(
      api.post<ApiResponse<import('@/types/crm').CrmCampaign>>(`/v1/crm/campaigns/${id}/schedule`, null, {
        params: { scheduledAt },
      }),
    ),

  createDealQuote: (dealId: number) =>
    unwrap(api.post<ApiResponse<number>>(`/v1/crm/deals/${dealId}/quote`)),

  listOpenTasks: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmTask[]>>('/v1/crm/tasks')),

  createTask: (body: {
    title: string;
    description?: string;
    customerId?: number;
    dealId?: number;
    leadId?: number;
    dueDate?: string;
    priority?: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/crm').CrmTask>>('/v1/crm/tasks', body)),

  completeTask: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmTask>>(`/v1/crm/tasks/${id}/complete`)),

  // Setup
  listScoringRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmScoringRule[]>>('/v1/crm/setup/scoring-rules')),
  createScoringRule: (body: Omit<import('@/types/crm').CrmScoringRule, 'id'>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmScoringRule>>('/v1/crm/setup/scoring-rules', body)),
  listAssignmentRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmAssignmentRule[]>>('/v1/crm/setup/assignment-rules')),
  listWorkflows: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmWorkflow[]>>('/v1/crm/setup/workflows')),
  listLeadSources: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmLeadSource[]>>('/v1/crm/setup/lead-sources')),
  createLeadSource: (body: { code: string; name: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmLeadSource>>('/v1/crm/setup/lead-sources', body)),
  listTerritories: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmTerritory[]>>('/v1/crm/setup/territories')),

  listEmailAccounts: () =>
    unwrap(api.get<ApiResponse<CrmEmailAccount[]>>('/v1/crm/setup/email-accounts')),

  createEmailAccount: (body: CrmEmailAccountRequest) =>
    unwrap(api.post<ApiResponse<CrmEmailAccount>>('/v1/crm/setup/email-accounts', body)),

  syncEmailAccounts: () =>
    unwrap(api.post<ApiResponse<number>>('/v1/crm/setup/email-accounts/sync')),

  googleOAuthAuthorizeUrl: () =>
    unwrap(api.get<ApiResponse<{ url: string; state: string }>>('/v1/crm/setup/email-accounts/oauth/google/authorize-url')),

  connectGoogleOAuth: (code: string, emailAddress?: string) =>
    unwrap(
      api.post<ApiResponse<CrmEmailAccount>>('/v1/crm/setup/email-accounts/oauth/google/connect', {
        code,
        emailAddress,
      }),
    ),

  listCustomerActivities: (customerId: number, limit = 50) =>
    unwrap(
      api.get<ApiResponse<import('@/types/crm').CrmActivityTimelineItem[]>>(
        `/v1/crm/customers/${customerId}/activities`,
        { params: { limit } }
      )
    ),
  listLeadActivities: (leadId: number, limit = 50) =>
    unwrap(
      api.get<ApiResponse<import('@/types/crm').CrmActivityTimelineItem[]>>(
        `/v1/crm/leads/${leadId}/activities`,
        { params: { limit } }
      )
    ),
  createActivity: (body: {
    customerId?: number;
    leadId?: number;
    dealId?: number;
    activityType: string;
    title: string;
    description?: string;
    reminderAt?: string;
    slaDueAt?: string;
    recurringRuleJson?: string;
    templateId?: number;
  }) => unwrap(api.post<ApiResponse<import('@/types/crm').CrmActivityTimelineItem>>('/v1/crm/activities', body)),

  timeline: (params: { customerId?: number; leadId?: number; dealId?: number; search?: string; limit?: number }) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmActivityTimelineItem[]>>('/v1/crm/timeline', { params })),

  listActivityTemplates: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmActivityTemplate[]>>('/v1/crm/activity-templates')),

  createActivityTemplate: (body: Partial<import('@/types/crm').CrmActivityTemplate>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmActivityTemplate>>('/v1/crm/activity-templates', body)),

  listActivitySlaRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmActivitySlaRule[]>>('/v1/crm/activity-sla-rules')),

  createActivitySlaRule: (body: Partial<import('@/types/crm').CrmActivitySlaRule>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmActivitySlaRule>>('/v1/crm/activity-sla-rules', body)),

  listDocuments: (params: { entityType: string; entityId: number }) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmDocument[]>>('/v1/crm/documents', { params })),

  createDocument: (body: Partial<import('@/types/crm').CrmDocument>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmDocument>>('/v1/crm/documents', body)),

  deleteDocument: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/crm/documents/${id}`)),

  listDocumentFolders: (params: { entityType: string; entityId: number }) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmDocumentFolder[]>>('/v1/crm/documents/folders', { params })),

  createDocumentFolder: (body: Partial<import('@/types/crm').CrmDocumentFolder>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmDocumentFolder>>('/v1/crm/documents/folders', body)),

  listCommunications: (params: { customerId?: number; leadId?: number; dealId?: number }) =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmCommunication[]>>('/v1/crm/communications', { params })),

  createCommunication: (body: Partial<import('@/types/crm').CrmCommunication>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmCommunication>>('/v1/crm/communications', body)),

  listSegments: (entityType = 'CUSTOMER') =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmSegment[]>>('/v1/crm/segmentation/segments', { params: { entityType } })),

  createSegment: (body: Partial<import('@/types/crm').CrmSegment>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSegment>>('/v1/crm/segmentation/segments', body)),

  listSavedFilters: (entityType = 'CUSTOMER') =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmSavedFilter[]>>('/v1/crm/segmentation/saved-filters', { params: { entityType } })),

  createSavedFilter: (body: Partial<import('@/types/crm').CrmSavedFilter>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmSavedFilter>>('/v1/crm/segmentation/saved-filters', body)),

  listFieldPermissions: (entityType = 'LEAD') =>
    unwrap(api.get<ApiResponse<import('@/types/crm').CrmFieldPermission[]>>('/v1/crm/setup/field-permissions', { params: { entityType } })),

  createFieldPermission: (body: Partial<import('@/types/crm').CrmFieldPermission>) =>
    unwrap(api.post<ApiResponse<import('@/types/crm').CrmFieldPermission>>('/v1/crm/setup/field-permissions', body)),
};
